// @flow
import type { State } from "@store/reducers";
import type { PaginationExtendedType, PropertiesSchema } from "@types";
import type {
  NotificationsSummaryItem,
  NotificationIndividualsRevisionHistoryItem,
  NotificationsContent,
  EmployeeNotification,
  HierarchyNotification,
  AddNotificationModal,
  NotificationsState,
  EmployeeRefType,
  NotificationAuditListType,
} from "@modules/shared/reducer/employee-notifications/types";
import type { EmployeeType } from "@modules/employee/types";
import { getPagination } from "@selectors";

export const getEmployeesNotificationAuditList = (state: State): NotificationAuditListType =>
  state.shared.employeeNotifications.notificationAudit.list;

export const getEmployeeNotifications = (state: State): NotificationsState =>
  state.shared.employeeNotifications;

export const getNotificationsSummaryItems = (state: State): Array<NotificationsSummaryItem> => {
  const summary = getEmployeeNotifications(state).summary || {};
  return summary ? Object.keys(summary).map(k => ({ description: k, value: summary[k] })) : [];
};

export const getNotificationsIndividuals = (
  state: State,
  employeeRefType: EmployeeRefType,
): Array<EmployeeNotification> =>
  (getEmployeeNotifications(state).individuals[employeeRefType] || {}).content;

export const getNotificationsIndividualsPagination = (
  state: State,
  employeeRefType: EmployeeRefType,
): PaginationExtendedType =>
  getPagination(getEmployeeNotifications(state).individuals[employeeRefType]);

export const selectNotificationsIndividualsPage = (
  state: State,
  employeeRefType: EmployeeRefType,
): number => getPagination(getEmployeeNotifications(state).individuals[employeeRefType]).pageNumber;

export const getNotificationsDepartments = (
  state: State,
  employeeRefType: EmployeeRefType,
): Array<HierarchyNotification> =>
  (getEmployeeNotifications(state).departments[employeeRefType] || {}).content;

export const getNotificationsDepartmentsPagination = (
  state: State,
  employeeRefType: EmployeeRefType,
): PaginationExtendedType =>
  getPagination(getEmployeeNotifications(state).departments[employeeRefType]);

export const selectNotificationsDepartmentsPage = (
  state: State,
  employeeRefType: EmployeeRefType,
): number => getPagination(getEmployeeNotifications(state).departments[employeeRefType]).pageNumber;

export const getAddNotificationModal = (state: State): AddNotificationModal =>
  getEmployeeNotifications(state).modals.addNotification;

export const getAddNotificationModalList = (state: State): Array<EmployeeType> =>
  getAddNotificationModal(state).list.content;

export const selectAddNotificationModalPagination = (state: State): PaginationExtendedType => {
  return getPagination(state.shared.employeeNotifications.modals.addNotification.list);
};

export const getNotificationSchema = (state: State): ?PropertiesSchema =>
  getEmployeeNotifications(state).notificationSchema;

export const getNotificationDateFields = (state: State): Object =>
  (state.form["notification-step-date-fields"] || {}).values || {};

export const getIndividualsRevisionHistoryModal = (
  state: Object,
): $Exact<{
  visible: boolean,
  data: NotificationsContent<NotificationIndividualsRevisionHistoryItem>,
}> => {
  return state.shared.employeeNotifications.modals.individualsRevisionHistory;
};
